import React, { useRef, useEffect, useState } from 'react';
import { User, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Helper function to get user initials for avatar
const getInitials = (name: string): string => {
  if (!name) return 'U';
  
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    // First and last name initials
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  // Single name - return first two letters
  return name.substring(0, 2).toUpperCase();
};

const UserMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, profileImageUrl } = useAuth();

  // Reset image state when profile image URL changes
  useEffect(() => {
    setImageError(false);
    setImageLoaded(false);
  }, [profileImageUrl]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => user ? setIsOpen(!isOpen) : navigate('/auth')}
        className="flex items-center space-x-2 focus:outline-none"
      >
        <span className="text-sm font-medium">
          {user?.name || user?.username}
        </span>
        <div className="relative w-8 h-8 flex-shrink-0">
          { !user ? (
            <div className="w-8 h-8 rounded-full border border-dark flex items-center justify-center">
              <User size={16}/>
            </div>
          ) : (
            <>
              {/* Placeholder/Fallback - Always visible during loading or on error */}
              {profileImageUrl && (!imageLoaded || imageError) && (
                <div className="absolute inset-0 w-8 h-8 rounded-full bg-accentPurple flex items-center justify-center">
                  <span className="text-sm text-text-inverse font-bold">
                    {getInitials(user?.name || user?.username || user?.email || '')}
                  </span>
                </div>
              )}
              
              {/* Actual Image */}
              {profileImageUrl && !imageError && (
                <img
                  ref={imageRef}
                  src={profileImageUrl}
                  alt={user.name || user.username}
                  className={`absolute inset-0 w-8 h-8 rounded-full object-cover transition-opacity duration-200 ${
                    imageLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                  onError={() => {
                    setImageError(true);
                    setImageLoaded(false);
                    if (imageRef.current) {
                      imageRef.current.style.display = 'none';
                    }
                  }}
                  onLoad={() => {
                    setImageLoaded(true);
                    setImageError(false);
                  }}
                  loading="lazy"
                />
              )}
              
              {/* Fallback for non-social users */}
              {!profileImageUrl && (
                <div className="w-8 h-8 rounded-full bg-accentPurple flex items-center justify-center">
                  <span className="text-sm text-text-inverse font-bold">
                    {user?.name?.charAt(0) || user?.username?.charAt(0)}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-50">
          {isAuthenticated && (
            <>
              <button
                onClick={() => navigate('/account')}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
              >
                <User size={16} className="mr-2" />
                Account
              </button>
              <hr className="my-1" />
              <button
                onClick={handleLogout}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-100 flex items-center"
              >
                <LogOut size={16} className="mr-2" />
                Sign Out
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default UserMenu;